// Settings → Groups panel (CTSK-240, 0.1.87). Lists every group, lets
// a groups:manage holder create / rename / soft-delete groups and
// add / remove members. Read side (list + detail) is gated on
// users:view server-side, same as the pre-existing GET /groups.
//
// Structure mirrors UsersPanel (list table + inline create form +
// per-row actions); the member-management drawer reuses the CTSK-245
// UserTypeahead the same way OrganizationsPanel's MembersPanel does.
// Server-side cap checks are authoritative; if the caller lacks
// groups:manage the mutation returns 403 and the toast surfaces it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch, apiList, apiSingle, ApiError } from '../lib/api.js';
import { toast } from './Toast.js';
import { UserTypeahead, type UserLite } from './UserTypeahead.js';
import { Users, Plus, Pencil, Trash2, X } from 'lucide-react';

interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
}

interface GroupMember {
  id: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
}

interface GroupDetail extends GroupRow {
  members: GroupMember[];
  roles: { id: string; name: string }[];
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid var(--border-strong)',
  borderRadius: 4,
};

export function GroupsPanel() {
  const qc = useQueryClient();

  // Create-group form state
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Inline edit state — one row at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Selected group for the member-management drawer.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const groupsQ = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiList<GroupRow>('/groups'),
  });

  const detailQ = useQuery({
    queryKey: ['groups', selectedId],
    queryFn: () => apiSingle<GroupDetail>(`/groups/${selectedId}`),
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['groups'] });
    // Membership counts feed the users table's groupIds too.
    qc.invalidateQueries({ queryKey: ['users'] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      apiSingle<GroupRow>('/groups', {
        method: 'POST',
        body: {
          name: newName.trim(),
          description: newDescription.trim() || null,
        },
      }),
    onSuccess: (data) => {
      toast.success(`Group created: ${data.name}`);
      setCreateOpen(false);
      setNewName('');
      setNewDescription('');
      invalidate();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to create group'),
  });

  const patchMut = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiSingle<GroupRow>(`/groups/${id}`, {
        method: 'PATCH',
        body: {
          name: editName.trim(),
          description: editDescription.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success('Group updated');
      setEditingId(null);
      invalidate();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to update group'),
  });

  const deleteMut = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<void>(`/groups/${id}`, { method: 'DELETE' }),
    onSuccess: (_d, { id }) => {
      toast.success('Group deleted');
      if (selectedId === id) setSelectedId(null);
      if (editingId === id) setEditingId(null);
      invalidate();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to delete group'),
  });

  const addMemberMut = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      apiSingle<{ groupId: string; userId: string }>(`/groups/${groupId}/members`, {
        method: 'POST',
        body: { userId },
      }),
    onSuccess: (_d, { groupId }) => {
      toast.success('Member added');
      qc.invalidateQueries({ queryKey: ['groups', groupId] });
      invalidate();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to add member'),
  });

  const removeMemberMut = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      apiFetch<void>(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: (_d, { groupId }) => {
      toast.success('Member removed');
      qc.invalidateQueries({ queryKey: ['groups', groupId] });
      invalidate();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to remove member'),
  });

  if (groupsQ.isError) {
    const err = groupsQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Listing groups requires the <code>users:view</code> capability.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load groups.</div>;
  }

  const allGroups = groupsQ.data?.data ?? [];
  const selected = selectedId ? detailQ.data : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Groups</h2>
          {!createOpen && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: 12,
                border: '1px solid var(--border-strong)',
                borderRadius: 6,
                background: 'var(--surface-raised)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <Plus size={12} /> New group
            </button>
          )}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
          {allGroups.length} group{allGroups.length === 1 ? '' : 's'} · click a row to manage members
        </p>

        {/* Inline create form — same shape as UsersPanel's. */}
        {createOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newName.trim()) return;
              createMut.mutate();
            }}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              padding: 12,
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 12,
            }}
          >
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Group name"
              required
              autoFocus
              style={{ ...inputStyle, flex: '1 1 180px', minWidth: 140 }}
            />
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description (optional)"
              style={{ ...inputStyle, flex: '2 1 260px', minWidth: 180 }}
            />
            <button
              type="submit"
              disabled={createMut.isPending || !newName.trim()}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'var(--accent-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: createMut.isPending ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
                setNewName('');
                setNewDescription('');
              }}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </form>
        )}

        {groupsQ.isLoading ? (
          <div style={{ color: 'var(--text-muted)' }}>Loading groups…</div>
        ) : allGroups.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            No groups yet. Create one to organize users into teams.
          </div>
        ) : (
          // Same fixed-layout pattern as UsersPanel — explicit colgroup
          // widths, nowrap + ellipsis, title attr for hover-to-see-full.
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 180 }} />
              <col />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 200 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Members</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allGroups.map((g) =>
                editingId === g.id ? (
                  <tr key={g.id}>
                    <td>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        aria-label="Group name"
                        autoFocus
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        aria-label="Group description"
                        placeholder="Description (optional)"
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{g.memberCount}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(g.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => patchMut.mutate({ id: g.id })}
                        disabled={patchMut.isPending || !editName.trim()}
                        style={{
                          padding: '4px 10px',
                          fontSize: 12,
                          background: 'var(--accent-primary)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          marginRight: 6,
                          fontWeight: 600,
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        style={{
                          padding: '4px 10px',
                          fontSize: 12,
                          background: 'transparent',
                          color: 'var(--text-muted)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={g.id}
                    onClick={() => setSelectedId(selectedId === g.id ? null : g.id)}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === g.id ? 'var(--surface-sunken)' : undefined,
                    }}
                  >
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }} title={g.name}>
                      {g.name}
                    </td>
                    <td
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: 12 }}
                      title={g.description ?? undefined}
                    >
                      {g.description ?? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Users size={12} /> {g.memberCount}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(g.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* CTSK-240 smoke fix (2026-07-30): wrap the two row
                          buttons in an inline-flex container. Same class
                          as CTSK-238's fix on UsersPanel — a `marginRight`-
                          between-two-block-ish-buttons layout gets squeezed
                          when the Created column auto-expands. Actions col
                          also bumped 150 → 200 in the colgroup above so
                          Edit + Delete fit horizontally with headroom. */}
                      <div
                        style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          title="Rename / edit description"
                          onClick={() => {
                            setEditingId(g.id);
                            setEditName(g.name);
                            setEditDescription(g.description ?? '');
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            fontSize: 12,
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 4,
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                          }}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          type="button"
                          title="Delete group (members are kept, group is removed)"
                          onClick={() => {
                            if (confirm(`Delete group "${g.name}"? Its ${g.memberCount} membership${g.memberCount === 1 ? '' : 's'} will be detached.`)) {
                              deleteMut.mutate({ id: g.id });
                            }
                          }}
                          disabled={deleteMut.isPending}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            fontSize: 12,
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 4,
                            background: 'transparent',
                            color: 'var(--danger-primary, #B3261E)',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </section>

      {/* Member management drawer for the selected group. */}
      {selectedId && (
        <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>
              Members{selected ? ` — ${selected.name}` : ''}
            </h3>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              aria-label="Close member panel"
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Add-member type-ahead — same CTSK-245 widget the org
              MembersPanel uses, but here the pick adds directly by
              userId (no email round-trip needed). */}
          <div style={{ maxWidth: 420, marginBottom: 12 }}>
            <AddMemberPicker
              disabled={addMemberMut.isPending}
              onPick={(user) => addMemberMut.mutate({ groupId: selectedId, userId: user.id })}
            />
          </div>

          {detailQ.isLoading ? (
            <div style={{ color: 'var(--text-muted)' }}>Loading members…</div>
          ) : !selected ? (
            <div style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 13 }}>Failed to load group detail.</div>
          ) : selected.members.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '8px 0' }}>
              No members yet. Search above to add one.
            </div>
          ) : (
            <table className="grid" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 200 }} />
                <col />
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Display name</th>
                  <th>Email</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selected.members.map((m) => (
                  <tr key={m.id}>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.displayName}>
                      {m.displayName}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.email}>
                      {m.email}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Remove ${m.displayName} from ${selected.name}?`)) {
                            removeMemberMut.mutate({ groupId: selectedId, userId: m.id });
                          }
                        }}
                        disabled={removeMemberMut.isPending}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          fontSize: 12,
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          background: 'transparent',
                          color: 'var(--danger-primary, #B3261E)',
                          cursor: 'pointer',
                        }}
                      >
                        <X size={12} /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selected && selected.roles.length > 0 && (
            <p style={{ margin: '12px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Linked roles: {selected.roles.map((r) => r.name).join(', ')} (managed via group-role links — not editable here).
            </p>
          )}
        </section>
      )}

      <section style={{ background: 'var(--surface-sunken)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
        <strong>Capability notes</strong>
        <ul style={{ margin: '6px 0 0 16px', padding: 0, lineHeight: 1.6 }}>
          <li><code>users:view</code> — list groups and see members (every built-in role holds it).</li>
          <li><code>groups:manage</code> — create / rename / delete groups and add / remove members (owner only by default; can be granted via custom role).</li>
        </ul>
        <p style={{ margin: '8px 0 0 0' }}>
          Deleting a group is a soft delete — memberships are detached from view but the record is kept for audit history.
        </p>
      </section>
    </div>
  );
}

/** Thin wrapper that owns the type-ahead's controlled input string and
 *  clears it after a pick lands, so the parent only sees `onPick`. */
function AddMemberPicker({ onPick, disabled }: { onPick: (user: UserLite) => void; disabled?: boolean }) {
  const [value, setValue] = useState('');
  return (
    <UserTypeahead
      value={value}
      onChange={setValue}
      onSelect={(user) => {
        onPick(user);
        setValue('');
      }}
      placeholder="Add member — search users by name or email…"
      ariaLabel="Add member to group"
      disabled={disabled}
    />
  );
}
